import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Search,
  RefreshCw,
  FileText,
  ShieldCheck,
  Clock,
  X,
  ArrowRight,
  Info,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";

const choices = {
  pricingMethod: [
    ["FIXED_PURCHASE_RATE", "Fixed purchase rate"],
    ["CHARMS_MARGIN", "CHARMS percentage margin"],
  ],
  percentageBasis: [
    ["MRP", "MRP"],
    ["SELLING_AMOUNT", "Selling amount after discount"],
  ],
  gstBasis: [
    ["INCLUSIVE", "Including GST"],
    ["EXCLUSIVE", "Excluding GST"],
  ],
  discountBearer: [
    ["BRAND", "Brand / supplier"],
    ["CHARMS", "CHARMS"],
    ["SHARED", "Shared"],
  ],
  liabilityTrigger: [
    ["RECEIPT", "When stock arrives"],
    ["SALE", "When an item sells"],
  ],
  settlementCycle: [
    ["INVOICE", "Invoice-wise"],
    ["WEEKLY", "Weekly"],
    ["MONTHLY", "Monthly"],
  ],
  paymentDeadline: [
    ["CREDIT_DAYS", "Credit days"],
    ["NEXT_MONTH_DAY", "A day of the following month"],
  ],
  unsoldReturns: [
    ["ALLOWED", "Allowed"],
    ["NOT_ALLOWED", "Not allowed"],
  ],
  excessReturnAdjustment: [
    ["CARRY_FORWARD", "Carry forward to next settlement"],
    ["SUPPLIER_REFUND", "Refund from supplier"],
  ],
};
const labels = {
  supplierInvoiceTiming: "SIS supplier GST invoice timing",
  sisAccountingPolicy: "SIS liability accounting policy",
  pricingMethod: "Pricing method",
  percentageBasis: "Percentage calculation basis",
  gstBasis: "GST basis for percentage",
  discountBearer: "Who bears discounts?",
  liabilityTrigger: "When does liability arise?",
  settlementCycle: "Settlement cycle",
  paymentDeadline: "Payment deadline method",
  unsoldReturns: "Unsold-stock returns",
  excessReturnAdjustment: "Returns exceeding unpaid balance",
  marginPercent: "CHARMS margin (%)",
  charmsDiscountPercent: "CHARMS share of discount (%)",
  creditDays: "Credit days",
  monthlyDueDay: "Due day next month (1–28)",
  returnWindowDays: "Unsold-stock return window (days)",
  returnConditions: "Unsold-stock return conditions",
  loyaltyBearer: "Loyalty redemption",
};
const control =
  "mt-2 block w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-primary-800 focus:ring-2 focus:ring-primary-100 disabled:bg-slate-50";
const blank = () => ({
  name: "",
  supplierId: "",
  brandId: "",
  type: "OUTRIGHT",
  status: "DRAFT",
  effectiveFrom: new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date()),
  effectiveTo: "",
  terms: { loyaltyBearer: "CHARMS" },
});
const human = (v) =>
  String(v)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
const valueLabel = (k, v) =>
  k === "loyaltyBearer"
    ? "Paid by CHARMS"
    : choices[k]?.find(([id]) => id === v)?.[1] || String(v);
const date = (v) =>
  v
    ? new Date(v.slice(0, 10) + "T12:00:00Z").toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No end date";
const unpack = (v) => (Array.isArray(v) ? v : v?.data || []);
function Field({ label, children, hint }) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      {children}
      {hint && (
        <span className="mt-1.5 block text-xs font-normal leading-5 text-slate-500">
          {hint}
        </span>
      )}
    </label>
  );
}
function Section({ number, title, description, children }) {
  return (
    <section className="border-b border-slate-100 px-5 py-6 sm:px-7">
      <div className="mb-5 flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-xs font-bold text-primary-800">
          {number}
        </span>
        <div>
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-xs leading-5 text-slate-500">{description}</p>
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">{children}</div>
    </section>
  );
}
function Badge({ status }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : status === "DRAFT" ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-500"}`}
    >
      {human(status)}
    </span>
  );
}

export default function CommercialAgreementsPage() {
  const { accessToken, user } = useAuth();
  const canManage = hasPermission(user, "suppliers.update");
  const [rows, setRows] = useState([]),
    [suppliers, setSuppliers] = useState([]),
    [brands, setBrands] = useState([]);
  const [form, setForm] = useState(blank),
    [id, setId] = useState(null),
    [editing, setEditing] = useState(false);
  const [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [masterError, setMasterError] = useState("");
  const [busy, setBusy] = useState(false),
    [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState(""),
    [type, setType] = useState(""),
    [page, setPage] = useState(1);
  const editor = useRef(null),
    message = useRef(null),
    locked = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(
        unpack(await apiRequest("/commercial-agreements", { accessToken })),
      );
    } catch (e) {
      setError(e.message || "Unable to load agreements.");
    } finally {
      setLoading(false);
    }
    const results = await Promise.allSettled([
      apiRequest("/suppliers", { accessToken }),
      apiRequest("/brands", { accessToken }),
    ]);
    setSuppliers(
      results[0].status === "fulfilled" ? unpack(results[0].value) : [],
    );
    setBrands(
      results[1].status === "fulfilled" ? unpack(results[1].value) : [],
    );
    setMasterError(
      results.some((r) => r.status === "rejected")
        ? "Some supplier or brand choices could not be loaded. Check master permissions or refresh before editing that scope."
        : "",
    );
  }, [accessToken]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (editing)
      editor.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [editing, id]);
  useEffect(() => {
    if (error || notice)
      message.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
  }, [error, notice]);
  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const term = (k, v) =>
    setForm((f) => ({ ...f, terms: { ...f.terms, [k]: v } }));
  const select = (k, hint) => (
    <Field label={labels[k]} hint={hint}>
      <select
        className={control}
        value={form.terms[k] || ""}
        onChange={(e) => term(k, e.target.value)}
      >
        <option value="">Not agreed yet</option>
        {choices[k].map(([v, title]) => (
          <option key={v} value={v}>
            {title}
          </option>
        ))}
      </select>
    </Field>
  );
  const number = (k, max) => (
    <Field label={labels[k]}>
      <input
        className={control}
        type="number"
        min={k === "monthlyDueDay" ? 1 : 0}
        max={max}
        step={k.includes("Percent") ? "0.01" : "1"}
        value={form.terms[k] ?? ""}
        onChange={(e) =>
          term(k, e.target.value === "" ? "" : Number(e.target.value))
        }
      />
    </Field>
  );
  const required = [
    "pricingMethod",
    "discountBearer",
    "liabilityTrigger",
    "settlementCycle",
    "paymentDeadline",
    "unsoldReturns",
    "excessReturnAdjustment",
  ];
  if (form.terms.pricingMethod === "CHARMS_MARGIN")
    required.push("marginPercent", "percentageBasis", "gstBasis");
  if (form.terms.discountBearer === "SHARED")
    required.push("charmsDiscountPercent");
  if (form.terms.paymentDeadline)
    required.push(
      form.terms.paymentDeadline === "CREDIT_DAYS"
        ? "creditDays"
        : "monthlyDueDay",
    );
  if (form.terms.unsoldReturns === "ALLOWED")
    required.push("returnWindowDays", "returnConditions");
  const missing = required.filter(
    (k) => form.terms[k] == null || String(form.terms[k]).trim() === "",
  );
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!status || r.status === status) &&
          (!type || r.type === type) &&
          [r.name, r.supplier?.displayName, r.brand?.name]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(search.trim().toLowerCase()),
      ),
    [rows, status, type, search],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / 12)),
    currentPage = Math.min(page, pages);
  const filter = (setter, v) => {
    setter(v);
    setPage(1);
  };
  function open(row) {
    if (
      editing &&
      !window.confirm("Discard unsaved changes and open another agreement?")
    )
      return;
    setError("");
    setNotice("");
    setId(row?.id || null);
    setForm(
      row
        ? {
            name: row.name,
            supplierId: row.supplierId || "",
            brandId: row.brandId || "",
            type: row.type,
            status: row.status,
            effectiveFrom: row.effectiveFrom.slice(0, 10),
            effectiveTo: row.effectiveTo?.slice(0, 10) || "",
            terms: { ...row.terms },
          }
        : blank(),
    );
    setEditing(true);
  }
  async function save(e) {
    e.preventDefault();
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body = {
        ...form,
        supplierId: form.supplierId || undefined,
        brandId: form.brandId || undefined,
        effectiveTo: form.effectiveTo || undefined,
        terms: Object.fromEntries(
          Object.entries(form.terms).filter(([, v]) => v !== "" && v != null),
        ),
      };
      await apiRequest(`/commercial-agreements${id ? "/" + id : ""}`, {
        method: id ? "PATCH" : "POST",
        accessToken,
        body,
      });
      setEditing(false);
      setId(null);
      setForm(blank());
      setNotice(
        form.status === "ACTIVE"
          ? "Agreement activated. Existing transaction terms are unchanged."
          : "Draft saved. You can finish the terms later.",
      );
      await load();
    } catch (e) {
      setError(e.message || "Unable to save agreement.");
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }
  async function retire(row) {
    if (
      locked.current ||
      !window.confirm(
        `Retire “${row.name}” for new transactions? Existing snapshots remain unchanged.`,
      )
    )
      return;
    locked.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await apiRequest(`/commercial-agreements/${row.id}/retire`, {
        method: "POST",
        accessToken,
      });
      setNotice("Agreement retired. Existing transaction terms are unchanged.");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }
  const scopeSupplier =
    suppliers.find((r) => r.id === form.supplierId)?.displayName ||
    (form.supplierId ? "Selected supplier" : "All suppliers");
  const scopeBrand =
    brands.find((r) => r.id === form.brandId)?.name ||
    (form.brandId ? "Selected brand" : "All brands");

  return (
    <main className="min-h-screen space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-primary-800">
            Supplier & brand terms
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Commercial agreements
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Manage buying terms without changing past transactions.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Refresh agreements"
            disabled={busy || loading}
            onClick={() => {
              setError("");
              load();
            }}
            className="rounded-xl border border-slate-200 bg-white p-3 text-slate-600 disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
          {canManage && (
            <button
              disabled={busy}
              onClick={() => {
                open();
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-4 py-3 text-sm font-semibold text-white shadow-sm"
            >
              <Plus size={17} />
              New agreement
            </button>
          )}
        </div>
      </header>
      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["ACTIVE", "Active versions", ShieldCheck],
          ["DRAFT", "Drafts to complete", FileText],
          ["INACTIVE", "Retired versions", Clock],
        ].map(([key, title, Icon]) => (
          <button
            key={key}
            onClick={() => filter(setStatus, status === key ? "" : key)}
            aria-pressed={status === key}
            className={`flex items-center gap-4 rounded-2xl border bg-white p-4 text-left ${status === key ? "border-primary-800 ring-1 ring-primary-800" : "border-slate-200 hover:border-primary-200"}`}
          >
            <span className="rounded-xl bg-primary-50 p-3 text-primary-800">
              <Icon size={20} />
            </span>
            <span>
              <span className="block text-xs text-slate-500">{title}</span>
              <span className="mt-1 block text-2xl font-semibold text-slate-900">
                {rows.filter((r) => r.status === key).length}
              </span>
            </span>
          </button>
        ))}
      </div>
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-xs leading-5 text-amber-900">
        <Info size={17} className="mt-0.5 shrink-0" />
        <p>
          Fixed-rate Outright/SOR agreements with invoice settlement and credit days
          support receipt and reviewed supplier-invoice accounting. SIS supports
          a separate reviewed gross-cost/no-input-GST-credit accrual for explicitly
          configured inclusive monthly agreements. Other margin, tax-component
          and periodic policies remain blocked. Saving terms alone does not post
          supplier dues or execute payments.
        </p>
      </div>
      {notice && (
        <p
          ref={message}
          role="status"
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
        >
          {notice}
        </p>
      )}
      {error && (
        <p
          ref={message}
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          {error}
        </p>
      )}
      {editing && canManage && (
        <form
          ref={editor}
          onSubmit={save}
          className="scroll-mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-5 sm:px-7">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">
                {id ? "Edit draft agreement" : "Create an agreement"}
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Save an incomplete draft, or finish the applicable terms before
                activation.
              </p>
            </div>
            <button
              type="button"
              disabled={busy}
              aria-label="Close agreement editor"
              onClick={() => {
                if (window.confirm("Discard unsaved changes?"))
                  setEditing(false);
              }}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-50"
            >
              <X size={20} />
            </button>
          </div>
          <fieldset disabled={busy} className="min-w-0">
            <div className="grid lg:grid-cols-[minmax(0,1fr)_300px]">
              <div>
                <Section
                  number="01"
                  title="Agreement scope"
                  description="A supplier + brand agreement takes priority over a general default."
                >
                  <div className="sm:col-span-2">
                    <Field label="Agreement name">
                      <input
                        required
                        maxLength={150}
                        placeholder="e.g. Reebok · Winter 2026 SIS"
                        value={form.name}
                        onChange={(e) => field("name", e.target.value)}
                        className={control}
                      />
                    </Field>
                  </div>
                  {[
                    [
                      "supplierId",
                      suppliers,
                      "Supplier",
                      "All suppliers — brand-wide agreement",
                    ],
                    [
                      "brandId",
                      brands,
                      "Brand",
                      "All brands — supplier-wide agreement",
                    ],
                  ].map(([key, values, title, empty]) => (
                    <Field key={key} label={title}>
                      <select
                        className={control}
                        value={form[key]}
                        onChange={(e) => field(key, e.target.value)}
                      >
                        <option value="">{empty}</option>
                        {values
                          .filter((r) => !r.deletedAt)
                          .map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.displayName || r.name}
                            </option>
                          ))}
                      </select>
                    </Field>
                  ))}
                  {!form.supplierId && !form.brandId && (
                    <p className="text-xs text-amber-800 sm:col-span-2">
                      Choose at least a supplier or brand. Leaving both empty is
                      not allowed.
                    </p>
                  )}
                  {masterError && (
                    <p
                      role="alert"
                      className="text-xs text-amber-800 sm:col-span-2"
                    >
                      {masterError}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <Field label="Buying type">
                      <select
                        className={control}
                        value={form.type}
                        onChange={(e) => field("type", e.target.value)}
                      >
                        <option value="OUTRIGHT">
                          Outright — direct purchase
                        </option>
                        <option value="SIS">SIS — Shop-in-Shop</option>
                        <option value="SOR">SOR — Sale or Return</option>
                      </select>
                    </Field>
                    <p className="mt-2 text-xs text-slate-500">
                      {form.type === "SIS"
                        ? "Sale-based supplier settlement with an agreed brand or supplier margin."
                        : form.type === "SOR"
                          ? "Liability begins on receipt, with agreed rights to return unsold stock."
                          : "Direct purchase under the agreed invoice and credit terms."}
                    </p>
                  </div>
                  {["effectiveFrom", "effectiveTo"].map((key) => (
                    <Field
                      key={key}
                      label={
                        key === "effectiveFrom"
                          ? "Effective from"
                          : "Effective to (optional)"
                      }
                    >
                      <input
                        type="date"
                        required={key === "effectiveFrom"}
                        min={
                          key === "effectiveTo" ? form.effectiveFrom : undefined
                        }
                        value={form[key]}
                        onChange={(e) => field(key, e.target.value)}
                        className={control}
                      />
                    </Field>
                  ))}
                </Section>
                <Section
                  number="02"
                  title="Pricing & discounts"
                  description="Choose the calculation method. No commercial values are assumed."
                >
                  {select("pricingMethod")}
                  {form.terms.pricingMethod === "CHARMS_MARGIN" && (
                    <>
                      {number("marginPercent", 100)}
                      {select("percentageBasis")}
                      {select("gstBasis")}
                    </>
                  )}
                  {form.terms.pricingMethod === "FIXED_PURCHASE_RATE" && (
                    <p className="self-center rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-500">
                      Enter each product’s fixed purchase rate on its PO/GRN
                      line.
                    </p>
                  )}
                  {select("discountBearer")}
                  {form.terms.discountBearer === "SHARED" && (
                    <>
                      {number("charmsDiscountPercent", 100)}
                      <p className="text-xs text-slate-500 sm:col-span-2">
                        The brand bears the remaining share.
                        {form.terms.charmsDiscountPercent !== "" &&
                          form.terms.charmsDiscountPercent != null &&
                          ` Brand share: ${100 - form.terms.charmsDiscountPercent}%.`}
                      </p>
                    </>
                  )}
                  <div className="rounded-xl bg-primary-50/70 p-3 text-xs leading-5 text-primary-800 sm:col-span-2">
                    <strong>Loyalty redemption stays with CHARMS.</strong> It
                    must not reduce the supplier’s entitlement.
                  </div>
                </Section>
                <Section
                  number="03"
                  title="Payment & settlement"
                  description="Liability timing and the payment due date are separate terms."
                >
                  {select(
                    "liabilityTrigger",
                    form.type === "SIS"
                      ? "SIS requires sale-based liability."
                      : "Outright and SOR require receipt-based liability.",
                  )}
                  {select("settlementCycle")}
                  {form.type === 'SIS' && <><Field label="SIS supplier GST invoice timing"><select className={control} value={form.terms.supplierInvoiceTiming || ''} onChange={e=>term('supplierInvoiceTiming',e.target.value)}><option value="">Choose actual agreement timing</option><option value="STOCK_RECEIPT">When stock arrives</option><option value="SALES_STATEMENT">After the sales statement</option></select></Field><Field label="SIS liability accounting policy"><select className={control} value={form.terms.sisAccountingPolicy || ''} onChange={e=>term('sisAccountingPolicy',e.target.value)}><option value="">Accountant review required — no default</option><option value="GROSS_COST_ACCRUAL_NO_ITC">Gross commercial cost accrual — no input GST credit</option><option value="INVOICE_COMPONENTS_REQUIRED">Actual invoice components required — posting blocked here</option></select></Field><p className="text-xs text-amber-800 sm:col-span-2">Invoice timing does not determine tax eligibility. The supported SIS posting path records reviewed gross cost and payable only. Actual invoice/GST adjustments remain separate; never record the same cost twice. Existing historical terms are not backfilled.</p></>}
                  {select("paymentDeadline")}
                  {form.terms.paymentDeadline === "CREDIT_DAYS" &&
                    number("creditDays", 365)}
                  {form.terms.paymentDeadline === "NEXT_MONTH_DAY" &&
                    number("monthlyDueDay", 28)}
                </Section>
                <Section
                  number="04"
                  title="Returns & adjustments"
                  description="Define unsold-stock returns and how excess credits are handled."
                >
                  {select(
                    "unsoldReturns",
                    form.type === "SOR"
                      ? "SOR must allow unsold-stock returns."
                      : undefined,
                  )}
                  {form.terms.unsoldReturns === "ALLOWED" &&
                    number("returnWindowDays", 3650)}
                  <div className="sm:col-span-2">
                    {select("excessReturnAdjustment")}
                  </div>
                  {form.terms.unsoldReturns === "ALLOWED" && (
                    <div className="sm:col-span-2">
                      <Field label="Unsold-stock return conditions">
                        <textarea
                          rows={3}
                          maxLength={2000}
                          placeholder="Original tags, packaging and return approval…"
                          value={form.terms.returnConditions || ""}
                          onChange={(e) =>
                            term("returnConditions", e.target.value)
                          }
                          className={control}
                        />
                      </Field>
                    </div>
                  )}
                </Section>
              </div>
              <aside className="border-l border-slate-100 bg-slate-50/70 p-5 sm:p-7">
                <div className="space-y-5 lg:sticky lg:top-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Agreement summary
                    </p>
                    <h3 className="mt-2 break-words text-lg font-semibold text-slate-900">
                      {form.name || "Untitled agreement"}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600">
                      {scopeSupplier}
                      <ArrowRight size={13} className="mx-2 inline" />
                      {scopeBrand}
                    </p>
                  </div>
                  <dl className="space-y-3 text-xs">
                    {[
                      ["Buying model", form.type],
                      ["Starts", date(form.effectiveFrom)],
                      ["Ends", date(form.effectiveTo)],
                      [
                        "Pricing",
                        form.terms.pricingMethod
                          ? valueLabel(
                              "pricingMethod",
                              form.terms.pricingMethod,
                            )
                          : "Not agreed",
                      ],
                      [
                        "Settlement",
                        form.terms.settlementCycle
                          ? valueLabel(
                              "settlementCycle",
                              form.terms.settlementCycle,
                            )
                          : "Not agreed",
                      ],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-3">
                        <dt className="text-slate-500">{k}</dt>
                        <dd className="text-right font-medium text-slate-800">
                          {v}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <div className="rounded-xl border border-slate-200 bg-white p-4">
                    <p className="text-sm font-semibold text-slate-800">
                      {missing.length
                        ? `${missing.length} terms still to complete`
                        : "Applicable fields filled"}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      {missing.length
                        ? "Save a draft now. Activation requires complete, valid terms."
                        : "The server checks validity, effective dates and conflicts on activation."}
                    </p>
                    {missing.length > 0 && (
                      <details className="mt-3 text-xs text-primary-800">
                        <summary className="cursor-pointer">
                          See missing terms
                        </summary>
                        <ul className="mt-2 list-disc space-y-1 pl-4">
                          {missing.map((k) => (
                            <li key={k}>{labels[k]}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                  <p className="text-xs leading-5 text-slate-500">
                    Active terms are locked. Retire and create a replacement to
                    change them. Existing PO/GRN snapshots stay unchanged.
                  </p>
                </div>
              </aside>
            </div>
            <footer className="flex flex-wrap items-end justify-between gap-4 border-t border-slate-200 bg-white px-5 py-4 sm:px-7">
              <Field label="Save as">
                <select
                  className={control}
                  value={form.status}
                  onChange={(e) => field("status", e.target.value)}
                >
                  <option value="DRAFT">Draft — finish later</option>
                  <option value="ACTIVE">Active — use for new documents</option>
                </select>
              </Field>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("Discard unsaved changes?"))
                      setEditing(false);
                  }}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600"
                >
                  Cancel
                </button>
                <button
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={busy}
                >
                  {busy
                    ? "Saving…"
                    : form.status === "ACTIVE"
                      ? "Save & activate"
                      : "Save draft"}
                  <ArrowRight size={16} />
                </button>
              </div>
            </footer>
          </fieldset>
        </form>
      )}
      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 p-5">
          <div>
            <h2 className="font-semibold text-slate-900">Agreement library</h2>
            <p className="mt-1 text-xs text-slate-500">
              {filtered.length} matching versions · Latest 500 versions loaded
            </p>
          </div>
          <div className="flex w-full flex-wrap gap-2 lg:w-auto">
            <label className="relative min-w-48 flex-1">
              <Search
                size={16}
                className="absolute left-3 top-3 text-slate-400"
              />
              <input
                aria-label="Search agreements"
                placeholder="Search name, supplier or brand"
                value={search}
                onChange={(e) => filter(setSearch, e.target.value)}
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary-800"
              />
            </label>
            <select
              aria-label="Filter by status"
              value={status}
              onChange={(e) => filter(setStatus, e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              {["ACTIVE", "DRAFT", "INACTIVE"].map((v) => (
                <option key={v} value={v}>
                  {human(v)}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by buying type"
              value={type}
              onChange={(e) => filter(setType, e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              <option value="">All buying types</option>
              {["OUTRIGHT", "SIS", "SOR"].map((v) => (
                <option key={v}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        {loading ? (
          <p className="p-12 text-center text-sm text-slate-500">
            Loading agreements…
          </p>
        ) : !filtered.length ? (
          <div className="px-5 py-14 text-center">
            <FileText className="mx-auto mb-3 text-slate-300" size={32} />
            <h3 className="font-semibold text-slate-800">
              {rows.length
                ? "No agreements match these filters"
                : "Your agreements start here"}
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {rows.length
                ? "Try another name, status or buying type."
                : "Create an agreement, then complete its commercial terms."}
            </p>
            {rows.length > 0 && (
              <button
                onClick={() => {
                  setSearch("");
                  setStatus("");
                  setType("");
                  setPage(1);
                }}
                className="mt-4 text-sm font-semibold text-primary-800"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered
              .slice((currentPage - 1) * 12, currentPage * 12)
              .map((row) => (
                <article key={row.id} className="px-5 py-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="break-words font-semibold text-slate-900">
                          {row.name}
                        </h3>
                        <Badge status={row.status} />
                        <span className="rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                          {row.type}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-slate-600">
                        {row.supplier?.displayName || "All suppliers"}{" "}
                        <span className="mx-1 text-slate-300">/</span>{" "}
                        {row.brand?.name || "All brands"}
                      </p>
                      <p className="mt-1 text-xs text-slate-400">
                        {date(row.effectiveFrom)} — {date(row.effectiveTo)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {canManage && row.status === "DRAFT" && (
                        <button
                          disabled={busy}
                          onClick={() => open(row)}
                          className="rounded-lg border border-primary-200 px-3 py-2 text-xs font-semibold text-primary-800"
                        >
                          Edit draft
                        </button>
                      )}
                      {canManage && row.status === "ACTIVE" && (
                        <button
                          disabled={busy}
                          onClick={() => retire(row)}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600"
                        >
                          Retire version
                        </button>
                      )}
                    </div>
                  </div>
                  <details className="mt-4">
                    <summary className="cursor-pointer text-xs font-semibold text-primary-800">
                      View agreed terms
                    </summary>
                    <dl className="mt-3 grid gap-x-8 gap-y-3 rounded-xl bg-slate-50 p-4 text-xs sm:grid-cols-2 xl:grid-cols-3">
                      {Object.entries(row.terms || {})
                        .filter(([, v]) => v != null && v !== "")
                        .map(([k, v]) => (
                          <div key={k}>
                            <dt className="text-slate-500">
                              {labels[k] || human(k)}
                            </dt>
                            <dd className="mt-1 whitespace-pre-wrap break-words font-medium text-slate-800">
                              {valueLabel(k, v)}
                            </dd>
                          </div>
                        ))}
                    </dl>
                  </details>
                </article>
              ))}
          </div>
        )}
        {filtered.length > 0 && (
          <footer className="flex items-center justify-between border-t border-slate-100 px-5 py-4 text-xs text-slate-500">
            <span>
              Page {currentPage} of {pages} · {filtered.length} versions
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
                className="rounded-lg border px-3 py-2 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                disabled={currentPage >= pages}
                onClick={() => setPage(currentPage + 1)}
                className="rounded-lg border px-3 py-2 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </footer>
        )}
      </section>
    </main>
  );
}
